import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAccount, useChainId, usePublicClient, useWriteContract } from "wagmi";
import { isAddress, parseEther, type Address } from "viem";
import { ERC721FactoryAbi } from "@atelier/abis";
import { useWizard, type WizardState } from "@/stores/wizard";
import { byId } from "@/lib/chains";
import { api } from "@/lib/api";

const ZERO_ADDR = "0x0000000000000000000000000000000000000000" as const;

const SET_SIGNER_ABI = [
  {
    type: "function",
    name: "setSigner",
    stateMutability: "nonpayable",
    inputs: [{ name: "newSigner", type: "address" }],
    outputs: [],
  },
] as const;

// All three deployable kinds (Drop, Simple, Badge) expose this same shape.
// Atelier1of1 doesn't have it by design (no contract-level owner).
const SET_CONTRACT_URI_ABI = [
  {
    type: "function",
    name: "setContractURI",
    stateMutability: "nonpayable",
    inputs: [{ name: "newContractURI", type: "string" }],
    outputs: [],
  },
] as const;

/**
 * Pulls the deployed collection address out of a receipt by reading the
 * `address` topic of the first log emitted by the factory. The factory's
 * CollectionCreated event is `(collection indexed, creator indexed, ...)`,
 * so topic[1] is the address (left-padded to 32 bytes).
 */
function collectionAddrFromReceipt(
  receipt: { logs: Array<{ address: `0x${string}`; topics: `0x${string}`[] }> },
  factory: `0x${string}`,
): `0x${string}` | null {
  for (const log of receipt.logs) {
    if (log.address.toLowerCase() === factory.toLowerCase()) {
      const addrTopic = log.topics[1];
      if (addrTopic) return (`0x${addrTopic.slice(26)}`) as `0x${string}`;
    }
  }
  return null;
}

/**
 * One hook driving the wizard's step-3 deploy flow for both Drop and Badge
 * kinds. Owns the on-chain side (factory.create*, plus the badge-specific
 * setSigner follow-up), the API registration, and the optional PFP project
 * association.
 *
 * Returns (status, deploy, isPending, factoryConfigured) so the step
 * component can stay a thin renderer.
 */
export function useDeployCollection() {
  const w = useWizard();
  const navigate = useNavigate();
  const { address } = useAccount();
  const chainId = useChainId();
  const chain = byId(chainId);
  const pc = usePublicClient({ chainId });
  const { writeContractAsync, isPending } = useWriteContract();
  const [status, setStatus] = useState<string | null>(null);

  const factoryConfigured =
    !!chain?.contracts.erc721Factory &&
    chain.contracts.erc721Factory !== ZERO_ADDR;

  /**
   * Build + upload an ERC-7572 contract-level metadata JSON. Marketplaces
   * (OpenSea, Magic Eden, Rarible…) call `contractURI()` on the deployed
   * contract and read this JSON to render the collection page (logo, banner,
   * description, royalties).
   *
   * Returns `undefined` if there's not enough info to build a meaningful
   * payload (no name, no image) — in that case we skip the on-chain
   * `setContractURI` step entirely instead of pinning an empty stub.
   */
  const uploadContractJson = async (): Promise<string | undefined> => {
    if (!w.name || !w.imageCid) return undefined;
    setStatus("Pinning contract metadata…");
    const royaltyReceiver = (
      isAddress(w.royaltyReceiver) ? w.royaltyReceiver : address
    ) as Address | undefined;
    const meta: Record<string, unknown> = {
      name: w.name,
      image: `ipfs://${w.imageCid}`,
    };
    if (w.symbol) meta.symbol = w.symbol;
    if (w.description) meta.description = w.description;
    if (w.bannerCid) meta.banner_image = `ipfs://${w.bannerCid}`;
    if (typeof w.royaltyBps === "number") meta.seller_fee_basis_points = w.royaltyBps;
    if (royaltyReceiver) meta.fee_recipient = royaltyReceiver;
    const { cid } = await api.uploadJson(meta);
    return cid;
  };

  const deployDrop = async (): Promise<Address | null> => {
    if (!address || !chain) return null;
    setStatus("Preparing transaction…");
    const royaltyReceiver = (
      isAddress(w.royaltyReceiver) ? w.royaltyReceiver : address
    ) as Address;
    const mintStart = w.mintStart
      ? Math.floor(new Date(w.mintStart).getTime() / 1000)
      : Math.floor(Date.now() / 1000) - 60;
    const mintEnd = w.mintEnd ? Math.floor(new Date(w.mintEnd).getTime() / 1000) : 0;
    const authorizedSigner = (
      w.allowlistEnabled && w.allowlistSigner ? w.allowlistSigner : ZERO_ADDR
    ) as Address;
    const publicMintAtSec =
      w.allowlistEnabled && w.publicMintAt
        ? Math.floor(new Date(w.publicMintAt).getTime() / 1000)
        : 0;
    const placeholderURI = w.revealEnabled && w.placeholderCid
      ? `ipfs://${w.placeholderCid}`
      : "";
    // The contract treats maxSupply=0 as "uncapped" — that magic value is
    // confined to this single line so callers further up the stack carry
    // the explicit `openEdition` boolean instead.
    const onChainMaxSupply = w.openEdition ? 0n : BigInt(w.maxSupply);
    // True blind mint: contract requires placeholder + empty baseURI so the
    // real metadata pointer never lands on chain pre-reveal. The real URI
    // (carried in w.baseUri client-side) gets passed to reveal() later.
    const blindMint = w.blindMint;
    const baseURI = blindMint ? "" : w.baseUri;
    const initParams = {
      name: w.name,
      symbol: w.symbol,
      maxSupply: onChainMaxSupply,
      mintPriceWei: parseEther(w.mintPriceEth || "0"),
      mintStart: BigInt(mintStart),
      mintEnd: BigInt(mintEnd),
      maxPerWallet: w.maxPerWallet,
      baseURI,
      royaltyReceiver,
      royaltyBps: BigInt(w.royaltyBps),
      owner: address as Address,
      authorizedSigner,
      publicMintAt: BigInt(publicMintAtSec),
      placeholderURI,
      blindMint,
    };
    const hash = await writeContractAsync({
      address: chain.contracts.erc721Factory,
      abi: ERC721FactoryAbi,
      functionName: "createDrop",
      args: [initParams],
      chainId,
    });
    setStatus("Waiting for confirmation…");
    if (!pc) throw new Error("no public client");
    const receipt = await pc.waitForTransactionReceipt({ hash });
    return collectionAddrFromReceipt(receipt, chain.contracts.erc721Factory);
  };

  const deployBadge = async (): Promise<Address | null> => {
    if (!address || !chain) return null;
    setStatus("Preparing transaction…");
    const sharedURI = w.imageCid ? `ipfs://${w.imageCid}` : "";
    const validUntil = w.badgeValidUntil
      ? Math.floor(new Date(w.badgeValidUntil).getTime() / 1000)
      : 0;
    const initParams = {
      name: w.name,
      symbol: w.symbol,
      sharedURI,
      // Temporary signer — rotated to the worker-derived address right after
      // we register and the worker derives a deterministic key for it.
      authorizedSigner: address as Address,
      maxSupply: BigInt(w.maxSupply),
      validUntil: BigInt(validUntil),
      soulbound: w.badgeSoulbound,
      oneShotPerAddress: w.badgeOneShot,
      owner: address as Address,
    };
    const hash = await writeContractAsync({
      address: chain.contracts.erc721Factory,
      abi: ERC721FactoryAbi,
      functionName: "createBadge",
      args: [initParams],
      chainId,
    });
    setStatus("Waiting for confirmation…");
    if (!pc) throw new Error("no public client");
    const receipt = await pc.waitForTransactionReceipt({ hash });
    return collectionAddrFromReceipt(receipt, chain.contracts.erc721Factory);
  };

  const registerBadge = async (collectionAddr: Address, contractCid?: string) => {
    await api.post(
      "/api/collections",
      {
        chainId,
        contractAddress: collectionAddr,
        kind: "badge",
        name: w.name,
        symbol: w.symbol,
        description: w.description || undefined,
        imageCid: w.imageCid,
        contractUri: contractCid ? `ipfs://${contractCid}` : undefined,
        subsidized: w.badgeSubsidized,
        soulbound: w.badgeSoulbound,
        authorizedSigner: address,
      },
      { auth: true },
    );

    setStatus("Fetching worker signer…");
    const info = await api.get<{ signerAddress: Address }>(
      `/api/badges/${chainId}/${collectionAddr}`,
    );

    setStatus("Authorizing worker signer on-chain…");
    const setHash = await writeContractAsync({
      address: collectionAddr,
      abi: SET_SIGNER_ABI,
      functionName: "setSigner",
      args: [info.signerAddress],
      chainId,
    });
    await pc!.waitForTransactionReceipt({ hash: setHash });
  };

  const registerDrop = async (collectionAddr: Address, contractCid?: string) => {
    const royaltyReceiver = (
      isAddress(w.royaltyReceiver) ? w.royaltyReceiver : address
    ) as Address;
    const mintStart = w.mintStart
      ? Math.floor(new Date(w.mintStart).getTime() / 1000)
      : Math.floor(Date.now() / 1000) - 60;
    const mintEnd = w.mintEnd ? Math.floor(new Date(w.mintEnd).getTime() / 1000) : 0;
    const publicMintAt = w.allowlistEnabled && w.publicMintAt
      ? Math.floor(new Date(w.publicMintAt).getTime() / 1000)
      : undefined;
    await api.post(
      "/api/collections",
      {
        chainId,
        contractAddress: collectionAddr,
        kind: "drop",
        name: w.name,
        symbol: w.symbol,
        description: w.description || undefined,
        imageCid: w.imageCid,
        bannerCid: w.bannerCid,
        contractUri: contractCid ? `ipfs://${contractCid}` : undefined,
        baseUri: w.baseUri || undefined,
        maxSupply: w.openEdition ? 0 : w.maxSupply,
        mintPriceWei: parseEther(w.mintPriceEth || "0").toString(),
        mintStart,
        mintEnd,
        royaltyBps: w.royaltyBps,
        royaltyReceiver,
        publicMintAt,
        placeholderCid: w.revealEnabled ? w.placeholderCid : undefined,
      },
      { auth: true },
    );
  };

  const associatePfp = async (collectionAddr: Address) => {
    if (!w.pfpProjectId) return;
    try {
      await api.post(
        `/api/pfp/${w.pfpProjectId}/associate`,
        { chainId, contractAddress: collectionAddr },
        { auth: true },
      );
    } catch {
      // best-effort; the PFP project's /projects/<id> baseURI keeps working
    }
  };

  const deploy = async () => {
    if (!address || !chain) return;
    try {
      // Build ERC-7572 contract.json BEFORE the on-chain deploy so we can
      // call setContractURI right after, in a single user-facing flow. The
      // pin happens once; if the wallet later rejects the deploy or the
      // setContractURI tx, the JSON stays in IPFS but isn't referenced —
      // harmless dead pin (CIDs are content-addressable, can't conflict).
      const contractCid = await uploadContractJson();

      const collectionAddr =
        w.kind === "badge" ? await deployBadge() : await deployDrop();
      if (!collectionAddr) {
        throw new Error("could not find deployed collection in receipt");
      }

      // setContractURI is a separate tx (no factory.create*-time field for
      // it). Skipped silently if uploadContractJson returned undefined
      // (e.g. the wizard had no image/name yet).
      if (contractCid) {
        setStatus("Setting contract URI on-chain…");
        const setUriHash = await writeContractAsync({
          address: collectionAddr,
          abi: SET_CONTRACT_URI_ABI,
          functionName: "setContractURI",
          args: [`ipfs://${contractCid}`],
          chainId,
        });
        if (!pc) throw new Error("no public client");
        await pc.waitForTransactionReceipt({ hash: setUriHash });
      }

      if (w.kind === "badge") {
        await registerBadge(collectionAddr, contractCid);
      } else {
        await registerDrop(collectionAddr, contractCid);
      }

      await associatePfp(collectionAddr);

      setStatus("Deployed! Redirecting…");
      setTimeout(() => navigate(`/${chain.shortName}/${collectionAddr}`), 800);
    } catch (err) {
      setStatus(`Error: ${(err as Error).message}`);
    }
  };

  return {
    deploy,
    status,
    isPending,
    factoryConfigured,
    chain,
    wizard: w as WizardState,
  };
}
